import { SmartNavHover } from './SmartNavInitializer';

/** Top-level desktop nav items; the lists are wrapped in <ul class="nav-list"> for valid list semantics. */
export const DESKTOP_NAV_ITEM_SELECTOR = 'header :is(nav.main-navigation, .top-navigation) > ul.nav-list > .nav-item';

export class MenuInitializer {
	/**
	 * Initializes the Menu.
	 *
	 * @returns void
	 */
	init() {
		this.initDesktopMenu();
		this.initMobileMenu();
	}

	/**
	 * Initializes all eventlisteners and logic needed for the desktop menu
	 *
	 * Needs to be called in the init() method
	 *
	 * @returns void
	 *
	 */
	initDesktopMenu() {
		const navItems = document.querySelectorAll(DESKTOP_NAV_ITEM_SELECTOR);

		navItems.forEach((navItem) => {
			const submenu = navItem.querySelector('&>.primary-sub-nav-container') as HTMLUListElement | null;
			if (!submenu) return;

			navItem.addEventListener('mouseenter', () => {
				if (!submenu.classList.contains('open')) {
					this.toggleSubMenu(submenu);
				}
			});

			navItem.addEventListener('mouseleave', () => {
				if (submenu.classList.contains('open')) {
					this.toggleSubMenu(submenu);
				}
			});

			// Add smartNav to every sub menu
			const firstSubMenu = navItem.querySelector<HTMLUListElement>('.primary-sub-nav-container > .container');
			if (firstSubMenu) {
				new SmartNavHover(firstSubMenu);
			}

			// Save image of primary nav item
			const navImage = navItem.querySelector('.nav-image') as HTMLImageElement | null;
			if (!navImage) return;

			const defaultImageSrc = navImage.src;

			const subNavItems = navItem.querySelectorAll('.sub-nav-item:has(>.sub-nav-item-link[data-image])');
			subNavItems.forEach((subNavItem) => {
				subNavItem.addEventListener('mouseenter', (event: Event) => {
					this.switchMenuImage(event.type, event.currentTarget as HTMLLIElement, navImage, defaultImageSrc);
				});

				subNavItem.addEventListener('mouseleave', (event: Event) => {
					this.switchMenuImage(event.type, event.currentTarget as HTMLLIElement, navImage, defaultImageSrc);
				});
			});
		});
	}

	/**
	 * Toggles the open state of the primary desktop submenu.
	 *
	 * @param {HTMLUListElement} subMenu The submenu that should be toggled
	 *
	 * @returns void
	 *
	 */
	toggleSubMenu(subMenu: HTMLUListElement) {
		const heightContainer = subMenu.querySelector<HTMLDivElement>(':scope > .container')!;

		if (subMenu.classList.contains('open')) {
			// Reset the height to 0
			subMenu.style.height = '0';
			subMenu.classList.remove('open');

			this.handleFurtherSubMenusClassRemove(subMenu);
		} else {
			// Calculate the height of the submenu
			const currentSubMenu = subMenu.querySelector<HTMLUListElement>(':scope > .container > .sub-nav-container')!;
			const navImage = subMenu.querySelector<HTMLImageElement>('.nav-image');

			let scrollHeight = currentSubMenu.scrollHeight;
			if (navImage) {
				scrollHeight = Math.max(currentSubMenu.scrollHeight, navImage.scrollHeight);
			}
			// Add 40px because we have a fixed padding of 20px on top and bottom of the container
			scrollHeight += 40;
			scrollHeight += (parseFloat(window.getComputedStyle(heightContainer).marginBlock) * 2) | 0;
			subMenu.style.height = scrollHeight + 'px';

			subMenu.classList.add('open');
			this.handleFurtherSubMenus(subMenu);
		}
	}

	handleFurtherSubMenus(toggledSubMenu: HTMLUListElement) {
		let currentSubMenu = toggledSubMenu.querySelector<HTMLUListElement>('.container > .sub-nav-container');
		if (currentSubMenu === null) return;
		currentSubMenu.classList.add('visible');

		MenuInitializer.calculateSubMenuHeight(toggledSubMenu);
	}

	handleFurtherSubMenusClassRemove(toggledSubMenu: HTMLUListElement) {
		const furtherSubMenus = toggledSubMenu.querySelectorAll<HTMLUListElement>('.sub-nav-container');

		furtherSubMenus.forEach((furtherSubMenu) => {
			furtherSubMenu.classList.remove('visible');
		});
	}

	// This method needs to be static and public, because the calculation needs to be triggered in the SmartNav logic.
	public static calculateSubMenuHeight(toggledSubMenu: HTMLUListElement) {
		let greatestSubMenuHeight = 0;

		const visibleSubMenus = toggledSubMenu.querySelectorAll<HTMLUListElement>('.sub-nav-container.visible');
		visibleSubMenus.forEach((visibleSubMenu) => {
			if (visibleSubMenu.scrollHeight > greatestSubMenuHeight) {
				greatestSubMenuHeight = visibleSubMenu.scrollHeight;
			}
		});

		const navImage = toggledSubMenu.querySelector<HTMLImageElement>('.nav-image');
		const heightContainer = toggledSubMenu.querySelector<HTMLDivElement>(':scope > .container')!;
		heightContainer.style.height = greatestSubMenuHeight + 'px';

		let scrollHeight = greatestSubMenuHeight;
		if (navImage) {
			scrollHeight = Math.max(greatestSubMenuHeight, navImage.scrollHeight);
		}

		// Add 40px beacuse we have a fixed padding of 20px on top and bottom of the container
		scrollHeight += 40;
		scrollHeight += (parseFloat(window.getComputedStyle(heightContainer).marginBlock) * 2) | 0;
		toggledSubMenu.style.height = scrollHeight + 'px';
	}

	/**
	 * Switches the image in the primary submenu, to the image related to the navigation item the user is hovering.
	 *
	 * @param {string} type The mouse event type
	 * @param {HTMLLIElement} subNavItem The list element that gets hovered
	 * @param {HTMLImageElement} navImage The image inside the visible submenu
	 *
	 * @returns void
	 *
	 */
	switchMenuImage(type: string, subNavItem: HTMLLIElement, navImage: HTMLImageElement, defaultImageSrc: string) {
		navImage.classList.add('hide');
		let imageSrc: string | null = null;

		switch (type) {
			case 'mouseleave':
				// Get image of parent submenu (if exists) because the mouseenter eventlistener of it is not getting triggered since it is the parent element and the event trigger already happend once
				const parentImageSrc = subNavItem.parentElement
					?.closest('.sub-nav-item')
					?.querySelector('.sub-nav-item-link[data-image]')
					?.getAttribute('data-image');

				imageSrc = parentImageSrc || defaultImageSrc;
				break;
			case 'mouseenter':
			default:
				// Get the source of the new image
				const imageSrcElement = subNavItem.querySelector('&>.sub-nav-item-link');
				if (!imageSrcElement) break;

				imageSrc = imageSrcElement.getAttribute('data-image');
				break;
		}

		// 200ms to wait for css transition
		setTimeout(() => {
			navImage.src = imageSrc || navImage.dataset.src!;
			navImage.srcset = imageSrc || navImage.dataset.src!;
			navImage.classList.remove('hide');
		}, 200);
	}

	/**
	 * Initializes all eventlisteners and logic needed for the mobile menu
	 *
	 * Needs to be called in the init() method
	 *
	 * @returns void
	 *
	 */
	initMobileMenu() {
		const burgerMenu = document.querySelector<HTMLDivElement>('.mobile-menu .main-menu .burger-menu-container');
		const openSubmenuButtons = document.querySelectorAll<HTMLButtonElement>(
			'.mobile-menu .expanded-menu .open-sub-nav'
		);
		const navBackButton = document.querySelector<HTMLDivElement>('.mobile-menu .main-menu .submenu-back-container');

		burgerMenu?.addEventListener('click', () => this.toggleMobileMenu('main-navigation'));

		// Add Eventlistener to open submenus
		openSubmenuButtons.forEach((button: HTMLButtonElement) => {
			button.addEventListener('click', () => {
				const submenu = button.nextElementSibling as HTMLUListElement | null;
				if (submenu) {
					this.openMobileSubMenu(submenu);
				}
			});
		});

		// Add Eventlistener to close the current submenu
		navBackButton?.addEventListener('click', () => {
			const openSubmenus = document.querySelectorAll<HTMLUListElement>(
				'.mobile-menu .expanded-menu .sub-nav-container.open'
			);
			const targetSubmenu = openSubmenus[openSubmenus.length - 1]; // Target the last open submenu
			if (targetSubmenu) {
				this.closeMobileSubMenu(targetSubmenu);
			}
		});

		// Add Eventlistener to open the mobile search
		const searchTrigger = document.querySelector<HTMLDivElement>('.mobile-menu .main-menu .search');
		searchTrigger?.addEventListener('click', () => this.toggleMobileMenu('search'));

		const languageSwitcherTrigger = document.querySelector<HTMLDivElement>(
			'.mobile-menu .main-menu .language-switcher'
		);
		languageSwitcherTrigger?.addEventListener('click', () => this.toggleMobileMenu('language-switcher'));
	}

	/**
	 * Toggles the expanded-state of the mobile menu
	 *
	 * @param {MenuType} type Which part of the of the mobile menu should be opened
	 * @returns void
	 *
	 */
	toggleMobileMenu(type: MenuType) {
		const mobileMenu = document.querySelector<HTMLDivElement>('.mobile-menu');
		if (!mobileMenu) return;
		mobileMenu.classList.toggle('expanded');

		const menuElements = mobileMenu.querySelectorAll('.expanded-menu>.container>*');
		menuElements?.forEach((element) => {
			element.classList.remove('open');
		});

		if (mobileMenu.classList.contains('expanded')) {
			const typeElement = mobileMenu.querySelector('.expanded-menu .' + type);
			typeElement?.classList.toggle('open');
		}
	}

	/**
	 * Switches to the given submenu level.
	 *
	 * @param {HTMLUListElement} submenu The submenu that you want to open
	 * @returns void
	 *
	 */
	openMobileSubMenu(submenu: HTMLUListElement) {
		submenu.classList.add('open');
	}

	/**
	 * Switches to the given submenu level.
	 *
	 * @param {HTMLUListElement} submenu The submenu that you want to open
	 * @returns void
	 *
	 */
	closeMobileSubMenu(submenu: HTMLUListElement) {
		submenu.classList.remove('open');
	}
}

type MenuType = 'main-navigation' | 'search' | 'language-switcher';
